/**
 * Jabra headset integration — WebHID call control.
 *
 * Bağlantı akışı:
 *   1. User Ses Ayarları → "Kulaklığı bağla" tıklar
 *   2. WebHID permission popup açılır (browser-managed)
 *   3. İzin verilince device handle alınır + buton subscription başlar
 *   4. Headset HID buton olayları → softphone action'larına bağlanır
 *
 * Hangi cihazlar?
 *   Jabra'nın resmi JS SDK'sı (@gnaudio/jabra-js) Easy Call Control mode'da
 *   tüm Engage / Evolve / Speak serilerini destekler. WebHID transport
 *   Chromium 89+ ve Tauri 2 WebView2'de çalışır.
 *
 * NOT: WebHID kullanıcı izni "user gesture" gerektirir — programatic olarak
 * `requestDevice()` çağırmak için butona tıklanması ŞART. Onay verildikten
 * sonra device bilgisi browser tarafından hatırlanır; sonraki açılışlarda
 * permission tekrar sorulmaz.
 */

import type { JanusSip } from './janus-sip';

export interface HeadsetState {
  /** WebHID this environment'ta destekleniyor mu? */
  supported: boolean;
  /** Cihaz bağlı mı? */
  connected: boolean;
  /** Cihaz etiketi (kullanıcıya gösterilir). */
  label: string;
  /** Son hata. */
  error: string | null;
}

type Subscriber = (s: HeadsetState) => void;

class HeadsetManager {
  private state: HeadsetState = {
    supported: typeof navigator !== 'undefined' && 'hid' in navigator,
    connected: false,
    label: '',
    error: null,
  };
  private subscribers = new Set<Subscriber>();
  private jabra: any = null;
  private device: any = null;
  private deviceSub: { unsubscribe: () => void } | null = null;
  private buttonSub: { unsubscribe: () => void } | null = null;
  private bound: JanusSip | null = null;
  /** State kapanış sırasında oluşturduğumuz callback'lerin reference'ı. */
  private clientUnsubs: Array<() => void> = [];

  /** softphone store init sırasında bir kere bağlanır. Auto-reconnect: WebHID
   *  daha önce izin verilmiş cihaza re-attach edebilir. */
  async bindClient(client: JanusSip): Promise<void> {
    this.bound = client;
    if (!this.state.supported) {
      this.update({ error: 'WebHID bu sürümde desteklenmiyor' });
      return;
    }
    try {
      await this.initJabra();
      // Otomatik reattach — daha önce permission alınmışsa devicelist event'inde
      // device görünür. requestDevice çağırmadığımız için sessizdir.
      this.attachExisting();
      this.bindClientEvents(client);
    } catch (e) {
      console.warn('[headset] init failed', e);
      this.update({ error: (e as Error).message });
    }
  }

  /** User-gesture içinden çağrılır (Settings'ten "Kulaklığı bağla" butonu). */
  async pair(): Promise<void> {
    if (!this.state.supported) return;
    if (!this.jabra) {
      try { await this.initJabra(); }
      catch (e) { this.update({ error: (e as Error).message }); return; }
    }
    try {
      // requestDevice browser permission prompt açar
      await this.jabra.requestDevice();
      this.attachExisting();
    } catch (e) {
      this.update({ error: (e as Error).message });
    }
  }

  /** Audio Settings'te göstermek için. */
  current(): HeadsetState { return { ...this.state }; }
  subscribe(fn: Subscriber): () => void {
    this.subscribers.add(fn);
    fn(this.current());
    return () => this.subscribers.delete(fn);
  }

  destroy(): void {
    this.buttonSub?.unsubscribe();
    this.deviceSub?.unsubscribe();
    this.clientUnsubs.forEach((u) => u());
    this.clientUnsubs = [];
    this.device = null;
    this.jabra = null;
    this.bound = null;
    this.update({ connected: false, label: '' });
  }

  private update(patch: Partial<HeadsetState>): void {
    this.state = { ...this.state, ...patch };
    this.subscribers.forEach((fn) => fn(this.current()));
  }

  private async initJabra(): Promise<void> {
    if (this.jabra) return;
    const mod: any = await import('@gnaudio/jabra-js');
    this.jabra = await mod.init({
      transport: mod.RequestedBrowserTransport.WEB_HID,
      appName: 'Arkphone',
    });
    // deviceList is an RxJS Observable — her cihaz değişiminde fire.
    this.deviceSub = this.jabra.deviceList.subscribe((devices: any[]) => {
      console.log('[headset] device list', devices?.length ?? 0);
      if (devices && devices.length > 0) {
        this.bindDevice(devices[0]);
      } else if (this.device) {
        // Cihaz çıkarıldı
        this.buttonSub?.unsubscribe();
        this.buttonSub = null;
        this.device = null;
        this.update({ connected: false, label: '' });
      }
    });
  }

  private attachExisting(): void {
    // jabra.deviceList observable şu anki listeyi anında verir.
    // initJabra'daki subscribe zaten ilk emit ile bindDevice çağrısı yapacak.
  }

  private bindDevice(device: any): void {
    if (this.device === device) return;
    this.buttonSub?.unsubscribe();
    this.device = device;
    const label = device?.name || 'Jabra Headset';
    this.update({ connected: true, label, error: null });
    console.log('[headset] bound:', label);

    // Easy Call Control — buton olayları
    this.buttonSub = device.buttonInput.subscribe((evt: any) => this.onButton(evt));
  }

  /** Headset HID button → softphone action mapping. */
  private onButton(evt: any): void {
    if (!this.bound) return;
    // Jabra-js Easy Call Control event types:
    //   OFF_HOOK / ON_HOOK — kulaklığın yeşil/kırmızı tuşu
    //   MUTE — mute toggle
    //   HOLD — hold/flash
    const t = (evt?.value || evt?.type || '').toString().toUpperCase();
    console.log('[headset] button', t);
    const sp = this.bound;
    if (t.includes('OFF_HOOK') || t.includes('ACCEPT')) {
      // Inbound ringing → accept, idle → no-op
      sp.accept();
    } else if (t.includes('ON_HOOK') || t.includes('REJECT') || t.includes('END')) {
      sp.hangup();
    } else if (t.includes('MUTE')) {
      sp.setMute(true); // toggle handled by softphone store wrapping
      // Note: setMute(true/false) — we don't know current state without
      // querying the store. Send to a small toggle relay:
      this.toggleMute();
    } else if (t.includes('HOLD') || t.includes('FLASH')) {
      this.toggleHold();
    }
  }

  /** Headset bizim soft state'i okuyamaz — toggle için store'a delege et. */
  private toggleMute(): void {
    // Wired through bound client by softphone store (which knows current mute).
    void this.clientUnsubs;
    (this as any)._toggleMute?.();
  }
  private toggleHold(): void {
    (this as any)._toggleHold?.();
  }

  /** softphone store mute/hold toggle'larını buradan tetiklenebilir kılar. */
  setHandlers(handlers: { toggleMute: () => void; toggleHold: () => void }): void {
    (this as any)._toggleMute = handlers.toggleMute;
    (this as any)._toggleHold = handlers.toggleHold;
  }

  /** Janus state event'lerini dinleyip headset'e geri bildir (LED, ringer). */
  private bindClientEvents(client: JanusSip): void {
    this.clientUnsubs.push(
      client.on('state', (s) => {
        if (!this.device) return;
        try {
          if (s === 'ringing') {
            this.device.ring?.();
          } else if (s === 'in_call') {
            this.device.stopRing?.();
            this.device.offHook?.();
          } else if (s === 'registered' || s === 'idle') {
            this.device.stopRing?.();
            this.device.onHook?.();
          }
        } catch (e) {
          console.warn('[headset] state sync failed', e);
        }
      }),
    );
  }
}

export const headset = new HeadsetManager();
